import React, { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';

import { useGetContractors, useDeleteContractor, useDeleteContractors } from 'api';

import CustomNoRowsOverlay from 'components/noRowsOverlay/customNoRowsOverlay';
import DataGridHeader from 'components/dataGridHeader';
import DataGridCell from 'components/dataGridCell';
import DashboardLayout from 'components/dashboardLayout/dashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { Box, Button, Loader } from 'components/ui';
import { ContractorFormModal } from 'components/containers';

import * as Styled from './Contractors.styles';
import DataGridActionsMenu from 'components/dataGridActionsMenu';
import { DeleteListItemConfirmationModal } from 'components/DeleteListItemConfirmationModal/DeleteListItemConfirmationModal';

export const Contractors = () => {
  const [editContractorData, setEditContractorData] = useState(null);

  const [deleteContractorGuids, setDeleteContractorGuids] = React.useState([]);
  const [selectedContractors, setSelectedContractors] = React.useState([]);
  const [deleteDialogConfirmationOpen, setDeleteDialogConfirmationOpen] = React.useState(false);

  const {
    data: contractorsData,
    refetch: refetchContractorsData,
    isLoading: isContractorsDataLoading,
  } = useGetContractors({
    offset: 0,
    limit: 999,
  });

  const { mutate: deleteContractor, isLoading: isContractorDeleting } = useDeleteContractor(
    deleteContractorGuids[0],
    {
      onSuccess: () => {
        refetchContractorsData();
        setDeleteContractorGuids([]);
        setDeleteDialogConfirmationOpen(false);
      },
    },
    {},
  );

  const { mutate: deleteContractors, isLoading: isContractorsDeleting } = useDeleteContractors(
    deleteContractorGuids,
    {
      onSuccess: () => {
        refetchContractorsData();
        setDeleteContractorGuids([]);
        setDeleteDialogConfirmationOpen(false);
      },
    },
    {},
  );

  const renderColHeader = (params) => <DataGridHeader>{params.colDef.headerName}</DataGridHeader>;

  const handleEditClick = (rowId) => {
    const data = contractorsData.filter((contractor) => contractor.id === rowId)[0];
    setEditContractorData(data);
  };

  const handleDeleteInvoiceClick = (rowId) => {
    setDeleteDialogConfirmationOpen(true);
    setDeleteContractorGuids([rowId]);      
  }

  const handleMenuOptionClick = (optionId, rowId) => {
    switch (optionId) {
      case 'delete':
        handleDeleteInvoiceClick(rowId);
        break;
      case 'edit':
        handleEditClick(rowId);
        break;
      default:
        break;
    }
  };

  const handleDeleteContractorAgreed = () => {
    if (deleteContractorGuids.length === 1) {
      deleteContractor();
    } else if (deleteContractorGuids.length > 1) {
      deleteContractors();
    }
  };

  const handleDeleteContractorDisagreed = () => {
    setDeleteContractorGuids([]);
    setDeleteDialogConfirmationOpen(false);
  };

  const columns = [
    {
      field: 'nip',
      headerName: 'NIP',
      width: 100,
      filterable: false,
      sortable: true,
      type: 'string',
      renderCell: (params) => <DataGridCell>{params.row.contractor.nip.nip}</DataGridCell>,
      renderHeader: renderColHeader,
    },
    {
      field: 'name',
      headerName: 'Nazwa',
      width: 250,
      filterable: false,
      sortable: false,
      type: 'string',
      renderCell: (params) => <DataGridCell main rowId={params.row.id} itemClicked={handleEditClick}>{params.row.contractor.name}</DataGridCell>,
      renderHeader: renderColHeader,
    },
    {
      field: 'address',
      headerName: 'Adres',
      flex: 1,
      minWidth: 200,
      filterable: false,
      sortable: false,
      type: 'string',
      renderCell: (params) => (
        <DataGridCell>
          {params.row.contractor.address.street} {params.row.contractor.address.houseNumber}{' '}
          {params.row.contractor.address.apartmentNumber && `lok. ${params.row.contractor.address.apartmentNumber}`}
          <br />
          {params.row.contractor.address.zipCode} {params.row.contractor.address.city}
        </DataGridCell>
      ),
      renderHeader: renderColHeader,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Akcje',
      width: 120,
      cellClassName: 'actions',
      headerAlign: 'right',
      align: 'right',
      renderHeader: renderColHeader,
      renderCell: (params) => (
        <DataGridActionsMenu
          remove
          rowId={params.row.id}
          handleMenuOptionClick={handleMenuOptionClick}
        />
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {deleteDialogConfirmationOpen && (
        <DeleteListItemConfirmationModal
          id="delete-confirmation-dialog"
          onConfirm={handleDeleteContractorAgreed}
          onClose={handleDeleteContractorDisagreed}
          isSaving={isContractorDeleting }
          title={selectedContractors?.length > 1 ? 'Usunąć kontrahentów?' : 'Usunąć kontrahenta?'}
          confirmText={selectedContractors?.length > 1 ? 'Tak, usuń kontrahentów' : 'Tak, usuń kontrahenta'}
          message={
            selectedContractors?.length > 1
              ? 'Czy na pewno chcesz usunąć kontrahentów? Tej operacji nie będzie można cofnąć.'
              : 'Czy na pewno chcesz usunąć kontrahenta? Tej operacji nie będzie można cofnąć.'
          }
        />
      )}
      <Styled.Wrapper>
        {isContractorsDataLoading ? (
          <Loader />
        ) : (
          <>
            <Button className="addContractorButton" onClick={() => setEditContractorData({})}>
              Dodaj kontrahenta
            </Button>

            <Box title="Kontrahenci">
              <DataGrid
                sx={{ '& > .MuiDataGrid-columnSeparator': { visibility: 'hidden' }, border: 'none' }}
                autoHeight
                rows={contractorsData || []}
                rowHeight={64}
                getRowHeight={() => 64}
                columns={columns}
                disableColumnFilter
                disableColumnMenu
                hideFooter
                components={{
                  NoRowsOverlay: CustomNoRowsOverlay,
                }}
              />
            </Box>
          </>
        )}

        {!!editContractorData && (
          <ContractorFormModal
            onClose={() => setEditContractorData(null)}
            contractorData={editContractorData}
            onSuccess={() => {
              setEditContractorData(null);
              refetchContractorsData();
            }}
          />
        )}
      </Styled.Wrapper>
    </DashboardLayout>
  );
};
